import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

// The "want custom packaging?" choice modal and the bundle picker it can reveal, mounted on
// the /give/collections shopping page.
//
// #custom-packaging-bundle-modal and its children are rendered by a partial shared with the
// collection detail page's own picker (custom_packaging_modal_controller.js). The two mount on
// views that nest the partial differently, so both reach it by plain id lookup rather than
// targets - a target-based lookup would only work for one of them.
const JQUERY_NAMESPACE = ".customPackagingCollectionsModal"

export default class extends Controller {
  static get targets() {
    return ["choiceModal", "option", "continueButton"]
  }

  static get values() {
    return { autoOpen: Boolean, bundleModalId: String }
  }

  static get classes() {
    return ["selected"]
  }

  // `data-custom-packaging-collections-modal-selected-class` is never set on this page's markup,
  // so this always falls back to the literal "selected" it replaces -- declared through the
  // Classes API instead of hard-coded at the three call sites below.
  get selectedClassName() {
    return this.hasSelectedClass ? this.selectedClass : "selected"
  }

  connect() {
    this.stripInterstitialParam()

    this.bundleModal = document.getElementById(this.bundleModalIdValue)
    this.searchBox = this.bundleModal.querySelector("#custom-packaging-bundle-search")
    this.bundleGrid = this.bundleModal.querySelector("#custom-packaging-bundle-grid")
    this.confirmButton = this.bundleModal.querySelector("#custom-packaging-bundle-confirm")
    this.clearButton = this.bundleModal.querySelector("#do-not-use-custom-packaging")
    this.updateUrl = this.bundleModal.dataset.updateUrl
    this.selectedChoice = null

    if (this.autoOpenValue) {
      this.autoOpenValue = false
      $(this.choiceModalTarget).modal("show")
    }

    // Appcues injects its launcher at the <body> level (root stacking context), so it sits
    // above our in-place modals regardless of z-index. Hide it while a modal is open by
    // toggling a body class (scoped so it never affects Appcues elsewhere).
    // R8: `shown.bs.modal`/`hidden.bs.modal` are events the Bootstrap modal jQuery plugin
    // announces -- native addEventListener cannot observe them, so the .on() binding itself stays
    // on jQuery's side. The class toggling inside each handler is plain DOM work and is native.
    ;[this.choiceModalTarget, this.bundleModal].forEach((modal) => {
      $(modal).on(`shown.bs.modal${JQUERY_NAMESPACE}`, () => document.body.classList.add("custom-packaging-modal-open"))
      $(modal).on(`hidden.bs.modal${JQUERY_NAMESPACE}`, () => {
        if (!this.choiceModalTarget.classList.contains("in") && !this.bundleModal.classList.contains("in")) {
          document.body.classList.remove("custom-packaging-modal-open")
        }
      })
    })

    // Bound so `disconnect()` can remove the exact same listener it added.
    this.handleSearchInput = () => this.filterBundles()
    this.searchBox.addEventListener("input", this.handleSearchInput)

    this.handleBundleGridClick = (event) => this.selectBundle(event)
    this.bundleGrid.addEventListener("click", this.handleBundleGridClick)

    this.handleConfirmClick = (event) => this.confirmBundle(event)
    this.confirmButton.addEventListener("click", this.handleConfirmClick)

    this.handleClearClick = (event) => {
      event.preventDefault()
      this.updateBuildCustomPackaging(null, () => this.reloadCollections())
    }
    this.clearButton.addEventListener("click", this.handleClearClick)

    // "Send without custom packaging" reset buttons, rendered in the tab headers -- outside
    // this controller's own element on every view that has them, hence the document-wide reach.
    this.sendWithoutButtons = Array.from(document.querySelectorAll(".send-without-custom-packaging"))
    this.handleSendWithoutClick = (event) => {
      event.preventDefault()
      this.updateBuildCustomPackaging(null, () => this.reloadCollections())
    }
    this.sendWithoutButtons.forEach((button) => button.addEventListener("click", this.handleSendWithoutClick))
  }

  disconnect() {
    $(this.choiceModalTarget).off(JQUERY_NAMESPACE)

    if (this.bundleModal) {
      $(this.bundleModal).off(JQUERY_NAMESPACE)
    }
    if (this.searchBox) {
      this.searchBox.removeEventListener("input", this.handleSearchInput)
    }
    if (this.bundleGrid) {
      this.bundleGrid.removeEventListener("click", this.handleBundleGridClick)
    }
    if (this.confirmButton) {
      this.confirmButton.removeEventListener("click", this.handleConfirmClick)
    }
    if (this.clearButton) {
      this.clearButton.removeEventListener("click", this.handleClearClick)
    }
    if (this.sendWithoutButtons) {
      this.sendWithoutButtons.forEach((button) => button.removeEventListener("click", this.handleSendWithoutClick))
    }
  }

  // Strip the `q` interstitial param so refreshing or navigating back doesn't reopen the modal
  stripInterstitialParam() {
    if (window.location.search.match(/[?&]q=/)) {
      const params = new URLSearchParams(window.location.search)
      params.delete("q")
      const querystring = params.toString()
      history.replaceState(null, "", window.location.pathname + (querystring ? `?${querystring}` : ""))
    }
  }

  // -- Choice modal ("Want to include custom packaging?") --

  selectChoice(event) {
    const option = event.currentTarget
    this.selectedChoice = option.dataset.choice
    this.optionTargets.forEach((other) => other.classList.toggle(this.selectedClassName, other === option))
    this.continueButtonTarget.disabled = false
  }

  continue(event) {
    event.preventDefault()

    if (this.selectedChoice === "yes") {
      // Wait for the choice modal to fully hide so the modal backdrops don't overlap
      $(this.choiceModalTarget).one(`hidden.bs.modal${JQUERY_NAMESPACE}`, () => $(this.bundleModal).modal("show"))
      $(this.choiceModalTarget).modal("hide")
    } else if (this.bundleModal.dataset.selectedId) {
      // The build already had a bundle (e.g. from a deep link) -- clear it and unfilter
      this.updateBuildCustomPackaging(null, () => this.reloadCollections())
    } else {
      $(this.choiceModalTarget).modal("hide")
    }
  }

  // -- Bundle selection modal --

  // All available bundles are already rendered, so filter them client-side by name
  filterBundles() {
    const query = this.searchBox.value.trim().toLowerCase()
    const cards = Array.from(this.bundleGrid.querySelectorAll(".custom-packaging-bundle-card"))
    let visibleCount = 0

    cards.forEach((card) => {
      const nameEl = card.querySelector(".bundle-name")
      const name = (nameEl ? nameEl.textContent : "").toLowerCase()
      const matches = !query || name.indexOf(query) !== -1
      card.classList.toggle("hidden", !matches)
      if (matches) {
        visibleCount += 1
      }
    })

    const noResults = this.bundleGrid.querySelector(".custom-packaging-bundle-no-results")
    if (noResults) {
      noResults.classList.toggle("hidden", cards.length === 0 || visibleCount > 0)
    }
  }

  // With a long grid the footer actions can sit below the fold, so bring them into view on
  // every selection. A no-op when they are already visible.
  revealFooterActions() {
    const footer = this.bundleModal.querySelector(".modal-footer")

    if (footer && footer.scrollIntoView) {
      footer.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }
  }

  // Only ready bundles with available inventory are selectable
  selectBundle(event) {
    const card = event.target.closest(".custom-packaging-bundle-card")
    if (!card || card.dataset.selectable !== "true") {
      return
    }

    card.classList.add(this.selectedClassName)
    Array.from(card.parentElement.children).forEach((sibling) => {
      if (sibling !== card) {
        sibling.classList.remove(this.selectedClassName)
      }
    })
    this.selectedBundleId = card.dataset.id
    this.confirmButton.disabled = false
    this.revealFooterActions()
  }

  confirmBundle(event) {
    event.preventDefault()
    if (!this.selectedBundleId) {
      return
    }

    this.updateBuildCustomPackaging(this.selectedBundleId, () => this.reloadCollections())
  }

  updateBuildCustomPackaging(customPackagingBundleId, onSuccess) {
    const options = RequestHelper.defaultPutOptions()
    options.headers = Object.assign({}, options.headers, {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "Accept": "application/json, text/plain, */*",
    })
    options.body = JSON.stringify({ custom_packaging_bundle_id: customPackagingBundleId })

    fetch(this.updateUrl, options)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to update custom packaging (${response.status})`)
        }
        onSuccess()
      })
      .catch((error) => console.warn(error))
  }

  reloadCollections() {
    // Preserve applied filters (the `q` interstitial param was already stripped on connect)
    const destination = window.location.pathname + window.location.search
    // S3: `pathname` is same-origin by construction in the ordinary case, but a pathname that
    // itself begins `//` makes this string a protocol-relative URL and so a cross-origin
    // destination. Asserted before navigating; the failing branch does not navigate, which leaves
    // the modal exactly as the user left it rather than silently sending them elsewhere.
    if (RequestHelper.isSameOriginUrl(destination)) {
      window.location.href = destination
    }
  }
}
